import React from "react";
import "style/Home.scss";

import { Link } from "react-router-dom";

import landing from "config/i18n/landing";
import useLang, { Lang } from "hooks/useLang";
import usePageTitle from "hooks/usePageTitle";

import Reveal from "components/Reveal";

import Rakete from "assets/video/Rakete.mp4";
import MsTraderStripe from "assets/image/MsTraderStripe.webp";
import JudoJamaHero from "assets/image/JudoJamaHero.webp"; // JAMA stripe (judo instance)
// JAMA app preview: the phone mockup of judojama.com, captured per language
// (transparent WebP) so the licence cards read in the visitor's language.
import JamaAppEn from "assets/image/jama-app/en.webp";
import JamaAppDe from "assets/image/jama-app/de.webp";
import JamaAppFr from "assets/image/jama-app/fr.webp";
import JamaAppEs from "assets/image/jama-app/es.webp";
import JamaAppIt from "assets/image/jama-app/it.webp";
import JamaAppPl from "assets/image/jama-app/pl.webp";
import JamaAppZh from "assets/image/jama-app/zh.webp";
import JamaAppJa from "assets/image/jama-app/ja.webp";
import JamaAppKo from "assets/image/jama-app/ko.webp";

const JAMA_APP: Record<Lang, string> = {
  en: JamaAppEn,
  de: JamaAppDe,
  fr: JamaAppFr,
  es: JamaAppEs,
  it: JamaAppIt,
  pl: JamaAppPl,
  zh: JamaAppZh,
  ja: JamaAppJa,
  ko: JamaAppKo,
};

/** Landing page routes, index-matched to landing.home.stripes / areas. */
export const AREA_ROUTES = ["/marksim", "/ms-trader", "/jama"];

const Home = () => {
  const lang = useLang();
  const t = landing[lang].home;
  usePageTitle();

  return (
    <div className="home">
      {/* Three 60° stripes — one per business area; each rises on hover. */}
      <header className="stripes">
        <span className="stripes__eyebrow">{t.eyebrow}</span>

        {t.stripes.map((stripe, index) => (
          <Link
            key={AREA_ROUTES[index]}
            to={AREA_ROUTES[index]}
            className={`stripe stripe--${index + 1}`}
          >
            <div className="stripe__media" aria-hidden="true">
              {index === 0 ? (
                <video autoPlay loop muted playsInline preload="metadata">
                  <source src={Rakete} type="video/mp4" />
                </video>
              ) : (
                <img
                  src={index === 1 ? MsTraderStripe : JudoJamaHero}
                  alt=""
                />
              )}
            </div>
            <div className="stripe__shade" aria-hidden="true" />
            {index === 2 ? (
              <span className="stripe__device" aria-hidden="true">
                <img src={JAMA_APP[lang]} alt="" />
              </span>
            ) : null}

            <span className="stripe__label">
              <span className="stripe__kicker">{stripe.kicker}</span>
              <span className="stripe__name">{stripe.name}</span>
              <span className="stripe__text">{stripe.text}</span>
              <span className="stripe__cta">
                {stripe.cta} <span aria-hidden="true">→</span>
              </span>
            </span>
          </Link>
        ))}
      </header>

      <main>
        <section className="lp-section lp-section--light">
          <div className="shell">
            <Reveal className="section-head section-head--center">
              <h2>
                {t.intro.title[0]}
                {/* full-width CJK colon already carries its own spacing */}
                {lang === "zh" || lang === "ja" ? "" : " "}
                <span className="accent">{t.intro.title[1]}</span>
              </h2>
              <p className="section-head__lead">{t.intro.text}</p>
              <p className="section-head__lead section-head__tagline">
                {t.intro.tagline}
              </p>
            </Reveal>

            <div className="lp-grid">
              {t.areas.map((area, index) => (
                <Reveal key={area.name} className="lp-cell" delay={index * 80}>
                  <article className="lp-card lp-card--area">
                    <div className="lp-card__meta">
                      <span className="lp-card__index">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="lp-card__kicker">{area.kicker}</span>
                    </div>
                    <h3>{area.name}</h3>
                    <p>{area.text}</p>
                    <ul className="lp-list">
                      {area.points.map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                    <Link className="lp-card__link" to={AREA_ROUTES[index]}>
                      {area.cta} <span aria-hidden="true">→</span>
                    </Link>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-section lp-section--muted">
          <div className="shell">
            <Reveal className="lp-cta">
              <div>
                <h2>{t.cta.title}</h2>
                <p>{t.cta.text}</p>
              </div>
              <div className="lp-cta__actions">
                <Link className="btn btn--primary" to="/contact">
                  {t.cta.button}
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Home;
